import Header from '../layouts/Header';
import React, { useState, useEffect } from 'react';
import {
  Container, Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge
} from 'react-bootstrap';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import axiosInstance from '../services/axiosInterceptor';

const DormitoryVacancy = () => {
  const [villages, setVillages] = useState([]);
  const [occupancySummary, setOccupancySummary] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    villageId: '',
    gender: ''
  });

    const [summaryGender, setSummaryGender] = useState('all');

  // Fetch all villages
  const fetchVillages = async () => {
    try {
      const response = await axiosInstance.get('/api/dormitory/villages');
      if (response.data.success) {
        setVillages(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load villages:', err);
    }
  };

  // Fetch occupancy summary
  const fetchOccupancySummary = async () => {
    try {
      const response = await axiosInstance.get('/api/dormitory/occupancy-summary');
      if (response.data.success) {
        setOccupancySummary(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load occupancy summary:', err);
    }
  };

  // Fetch vacancies
  const fetchVacancies = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.villageId) params.append('villageId', filters.villageId);
      if (filters.gender) params.append('gender', filters.gender);

      const response = await axiosInstance.get(
        `/api/dormitory/vacancies?${params.toString()}`
      );
      if (response.data.success) {
        setVacancies(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลหอพักว่างไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVillages();
    fetchOccupancySummary();
  }, []);

  useEffect(() => {
    fetchVacancies();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReset = () => {
    setFilters({
      villageId: '',
      gender: ''
    });
  };

  const genderBadge = (gender) => {
    if (gender === 'm') return <Badge bg="primary">👨 ชาย</Badge>;
    return <Badge bg="danger">👩 หญิง</Badge>;
  };

  const getOccupancyPercentage = (current, total) => {
    if (total === 0) return 0;
    return Math.round((current / total) * 100);
  };

  const getStatusColor = (current, total) => {
    const percentage = getOccupancyPercentage(current, total);
    if (percentage >= 100) return 'danger';
    if (percentage >= 80) return 'warning';
    if (percentage >= 50) return 'info';
    return 'success';
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const scopeText = summaryGender === 'all' ? 'ทั้งหมด' : summaryGender === 'm' ? 'ชาย' : 'หญิง';

    const summaryRows = occupancySummary
      .filter(s => summaryGender === 'all' || s.village_gender === summaryGender)
      .map(s => ({
        เขตหอพัก: s.village_name,
        เพศ: s.village_gender === 'm' ? 'ชาย' : 'หญิง',
        จำนวนอาคาร: s.building_count,
        ความจุรวม: s.total_capacity,
        เข้าพักแล้ว: s.current_occupancy,
        ว่าง: s.available_spaces,
        อัตราเข้าพักเปอร์เซ็นต์: getOccupancyPercentage(s.current_occupancy, s.total_capacity)
      }));

    const vacancyRows = vacancies.map(v => ({
      รหัสอาคาร: v.dorm_code,
      ชื่ออาคาร: v.dorm_name,
      หมู่หอพัก: v.village_name,
      เพศ: v.village_gender === 'm' ? 'ชาย' : 'หญิง',
      ความจุรวม: v.capacity_total,
      เข้าพักแล้ว: v.current_occupancy,
      ว่าง: v.vacant_count,
      อัตราเข้าพักเปอร์เซ็นต์: getOccupancyPercentage(v.current_occupancy, v.capacity_total)
    }));

    const metaRows = [{
      รายงาน: 'รายงานห้องพักว่างและอัตราการเข้าพัก',
      ขอบเขตข้อมูล: scopeText,
      วันเวลาที่ออกรายงาน: new Date().toLocaleString('th-TH')
    }];

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(metaRows), 'ข้อมูลรายงาน');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'ภาพรวมเขตหอพัก');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(vacancyRows), 'อาคารที่ยังว่าง');
    XLSX.writeFile(wb, `dormitory-vacancy-${Date.now()}.xlsx`);
  };

  const handleExportPdf = async () => {
    const { default: html2canvas } = await import('html2canvas');
    const scopeText = summaryGender === 'all' ? 'ทั้งหมด' : summaryGender === 'm' ? 'ชาย' : 'หญิง';

    const summaryRows = occupancySummary
      .filter(s => summaryGender === 'all' || s.village_gender === summaryGender)
      .map(s => ({
        village_name: s.village_name,
        village_gender: s.village_gender === 'm' ? 'ชาย' : 'หญิง',
        building_count: s.building_count,
        total_capacity: s.total_capacity,
        current_occupancy: s.current_occupancy,
        available_spaces: s.available_spaces,
        occupancy_rate: getOccupancyPercentage(s.current_occupancy, s.total_capacity)
      }));

    const reportRoot = document.createElement('div');
    reportRoot.style.position = 'fixed';
    reportRoot.style.left = '-99999px';
    reportRoot.style.top = '0';
    reportRoot.style.width = '1320px';
    reportRoot.style.padding = '24px';
    reportRoot.style.background = '#ffffff';
    reportRoot.style.color = '#111111';
    reportRoot.style.fontFamily = 'Tahoma, Arial, sans-serif';

    const summaryTableHtml = summaryRows.map((row) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;">${row.village_name}</td>
        <td style="border:1px solid #ddd;padding:6px;">${row.village_gender}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${row.building_count}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${row.total_capacity}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${row.current_occupancy}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${row.available_spaces}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${row.occupancy_rate}</td>
      </tr>
    `).join('');

    const vacancyTableHtml = vacancies.map((v) => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;">${v.dorm_code}</td>
        <td style="border:1px solid #ddd;padding:6px;">${v.dorm_name}</td>
        <td style="border:1px solid #ddd;padding:6px;">${v.village_name}</td>
        <td style="border:1px solid #ddd;padding:6px;">${v.village_gender === 'm' ? 'ชาย' : 'หญิง'}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.capacity_total}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.current_occupancy}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${v.vacant_count}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${getOccupancyPercentage(v.current_occupancy, v.capacity_total)}</td>
      </tr>
    `).join('');

    reportRoot.innerHTML = `
      <h2 style="margin:0 0 8px 0;">รายงานห้องพักว่างและอัตราการเข้าพัก</h2>
      <div style="margin:0 0 4px 0;">ขอบเขตข้อมูล: ${scopeText}</div>
      <div style="margin:0 0 16px 0;">วันเวลาที่ออกรายงาน: ${new Date().toLocaleString('th-TH')}</div>

      <h3 style="margin:12px 0 8px 0;">ภาพรวมเขตหอพัก</h3>
      <table style="border-collapse:collapse;width:100%;margin-bottom:16px;">
        <thead>
          <tr>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">เขตหอพัก</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">เพศ</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">จำนวนอาคาร</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">ความจุรวม</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">เข้าพักแล้ว</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">ว่าง</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">อัตราเข้าพัก %</th>
          </tr>
        </thead>
        <tbody>
          ${summaryTableHtml || '<tr><td colspan="7" style="border:1px solid #ddd;padding:6px;text-align:center;">ไม่พบข้อมูล</td></tr>'}
        </tbody>
      </table>

      <h3 style="margin:12px 0 8px 0;">อาคารที่ยังว่าง</h3>
      <table style="border-collapse:collapse;width:100%;">
        <thead>
          <tr>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">รหัสอาคาร</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">ชื่ออาคาร</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">หมู่หอพัก</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:left;">เพศ</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">ความจุรวม</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">เข้าพักแล้ว</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">ว่าง</th>
            <th style="border:1px solid #ddd;padding:6px;background:#f1f3f5;text-align:right;">อัตราเข้าพัก %</th>
          </tr>
        </thead>
        <tbody>
          ${vacancyTableHtml || '<tr><td colspan="8" style="border:1px solid #ddd;padding:6px;text-align:center;">ไม่พบข้อมูล</td></tr>'}
        </tbody>
      </table>
    `;

    document.body.appendChild(reportRoot);

    const canvas = await html2canvas(reportRoot, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff'
    });

    document.body.removeChild(reportRoot);

    const imgData = canvas.toDataURL('image/png');
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    if (imgHeight <= pageHeight - 30) {
      doc.addImage(imgData, 'PNG', 20, 15, imgWidth, imgHeight);
    } else {
      let heightLeft = imgHeight;
      let position = 15;
      doc.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - 30);

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 15;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - 30);
      }
    }

    doc.save(`dormitory-vacancy-${Date.now()}.pdf`);
  };

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h2 className="mb-0">รายงานห้องพักว่างและอัตราการเข้าพัก</h2>
          <div className="d-flex gap-2">
            <Button variant="outline-success" onClick={handleExportExcel}>
              ส่งออก Excel
            </Button>
            <Button variant="outline-danger" onClick={handleExportPdf}>
              ส่งออก PDF
            </Button>
          </div>
        </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {/* Overview Cards */}
        <div className="d-flex align-items-center gap-3 mb-3">
          <strong>แสดง:</strong>
          {['all', 'm', 'f'].map(g => (
            <Form.Check
              key={g}
              inline
              type="radio"
              label={g === 'all' ? 'ทั้งหมด' : g === 'm' ? '👨 ชาย' : '👩 หญิง'}
              name="summaryGender"
              value={g}
              checked={summaryGender === g}
              onChange={() => setSummaryGender(g)}
            />
          ))}
        </div>
        <Row className="mb-4">
          {occupancySummary.filter(s => summaryGender === 'all' || s.village_gender === summaryGender).map(summary => {
          const occupancyPct = getOccupancyPercentage(summary.current_occupancy, summary.total_capacity) || 0;
          
          return (
            <Col md={6} lg={2} key={summary.village_id} className="mb-3">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0">{summary.village_name}</h6>
                    {genderBadge(summary.village_gender)}
                  </div>
                  <p className="text-muted small mb-3">{summary.building_count} อาคาร</p>

                  <div className="mb-3">
                    <div className="progress" style={{ height: '25px' }}>
                      <div
                        className={`progress-bar bg-${getStatusColor(summary.current_occupancy, summary.total_capacity)}`}
                        style={{ width: `${occupancyPct}%` }}
                      >
                        {occupancyPct}%
                      </div>
                    </div>
                  </div>

                  <Row className="text-center g-2">
                    <Col xs={6}>
                      <small className="text-muted">เข้าพักแล้ว</small>
                      <p className="mb-0"><strong>{summary.current_occupancy}</strong></p>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted">ว่าง</small>
                      <p className="mb-0"><strong>{summary.available_spaces}</strong></p>
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <p className="text-muted small mb-0">ความจุรวม: {summary.total_capacity}</p>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Filter Section */}
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">กรองข้อมูลหอพักว่าง</h5>
          <Row>
            <Col md={5}>
              <Form.Group>
                <Form.Label>หมู่หอพัก</Form.Label>
                <Form.Select
                  name="villageId"
                  value={filters.villageId}
                  onChange={handleFilterChange}
                >
                  <option value="">-- ทุกหมู่หอพัก --</option>
                  {villages.map(v => (
                    <option key={v.village_id} value={v.village_id}>
                      {v.village_name} ({v.village_gender === 'm' ? '👨 ชาย' : '👩 หญิง'})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>เพศ</Form.Label>
                <Form.Select
                  name="gender"
                  value={filters.gender}
                  onChange={handleFilterChange}
                >
                  <option value="">-- ทุกเพศ --</option>
                  <option value="m">👨 เฉพาะชาย</option>
                  <option value="f">👩 เฉพาะหญิง</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={handleReset}
                >
                  ล้างตัวกรอง
                </Button>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Vacancies Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">อาคารหอพักที่ยังว่าง</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">กำลังโหลด...</span>
              </Spinner>
            </div>
          ) : vacancies.length === 0 ? (
            <Alert variant="info" className="mb-0">
              ไม่พบหอพักว่างที่ตรงกับเงื่อนไขที่เลือก
            </Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '12%' }}>รหัสอาคาร</th>
                  <th style={{ width: '20%' }}>ชื่ออาคาร</th>
                  <th style={{ width: '15%' }}>หมู่หอพัก</th>
                  <th style={{ width: '12%' }}>เพศ</th>
                  <th style={{ width: '12%' }}>ความจุรวม</th>
                  <th style={{ width: '12%' }}>เข้าพักแล้ว</th>
                  <th style={{ width: '17%' }}>ระดับการเข้าพัก</th>
                </tr>
              </thead>
              <tbody>
                {vacancies.map(building => {
                  const occupied = building.current_occupancy;
                  const total = building.capacity_total;
                  const vacant = building.vacant_count;
                  const occupancyPct = getOccupancyPercentage(occupied, total);
                  const statusColor = getStatusColor(occupied, total);

                  return (
                    <tr key={building.dorm_id}>
                      <td><strong>{building.dorm_code}</strong></td>
                      <td>{building.dorm_name}</td>
                      <td>{building.village_name}</td>
                      <td>{genderBadge(building.village_gender)}</td>
                      <td className="text-center">{total}</td>
                      <td className="text-center">{occupied}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ width: '70px', minWidth: '70px' }}>
                            <div className="progress bg-light" style={{ height: '18px' }}>
                              <div
                                className={`progress-bar bg-${statusColor}`}
                                style={{ width: `${occupancyPct}%` }}
                              >
                                {occupancyPct}%
                              </div>
                            </div>
                          </div>
                          <small className="text-nowrap">
                            ว่าง {vacant} ที่
                          </small>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {vacancies.length > 0 && (
            <div className="mt-3 p-3 bg-light rounded">
              <small className="text-muted">
                <strong>ผลลัพธ์ทั้งหมด:</strong> {vacancies.length} อาคารที่ยังมีที่ว่าง
              </small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Legend */}
      <Card className="mt-4">
        <Card.Body>
          <h6 className="mb-3">คำอธิบายระดับการเข้าพัก</h6>
          <Row>
            <Col md={3}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div 
                  className="progress flex-shrink-0" 
                  style={{ width: '40px', height: '20px' }}
                >
                  <div 
                    className="progress-bar bg-success" 
                    style={{ width: '25%' }}
                  />
                </div>
                <small>0-50%: มีที่ว่างมาก</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div 
                  className="progress flex-shrink-0" 
                  style={{ width: '40px', height: '20px' }}
                >
                  <div 
                    className="progress-bar bg-info" 
                    style={{ width: '50%' }}
                  />
                </div>
                <small>50-80%: เริ่มมีผู้พักมากขึ้น</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <div 
                  className="progress flex-shrink-0" 
                  style={{ width: '40px', height: '20px' }}
                >
                  <div 
                    className="progress-bar bg-warning" 
                    style={{ width: '80%' }}
                  />
                </div>
                <small>80-100%: เหลือที่ว่างน้อย</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center gap-2">
                <div 
                  className="progress flex-shrink-0" 
                  style={{ width: '40px', height: '20px' }}
                >
                  <div 
                    className="progress-bar bg-danger" 
                    style={{ width: '100%' }}
                  />
                </div>
                <small>100%: เต็มความจุ</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryVacancy;
